// Instrumented Merge Sort — produces ONE unified step model that drives
// everything: the 3D recursion tree, the highlighted code line, the live call
// stack, and the step description. This is the core of the "3D Lab" feature.
//
// The displayed code — line numbers MUST match the `line` field emitted below.
// Blank line 8 separates the two functions and is counted by the code panel.

export const MERGE_SORT_CODE = `function mergeSort(arr, lo, hi) {
  if (lo >= hi) return;              // base case: 0 or 1 element
  const mid = (lo + hi) >> 1;        // middle index
  mergeSort(arr, lo, mid);           // sort the left half
  mergeSort(arr, mid + 1, hi);       // sort the right half
  merge(arr, lo, mid, hi);           // merge the two sorted halves
}

function merge(arr, lo, mid, hi) {
  const L = arr.slice(lo, mid + 1);      // copy left half
  const R = arr.slice(mid + 1, hi + 1);  // copy right half
  let i = 0, j = 0, k = lo;              // read/write pointers
  while (i < L.length && j < R.length) {
    if (L[i] <= R[j]) arr[k++] = L[i++]; // take from left
    else              arr[k++] = R[j++]; // take from right
  }
  while (i < L.length) arr[k++] = L[i++]; // drain remaining left
  while (j < R.length) arr[k++] = R[j++]; // drain remaining right
}`;

// Returns { steps, nodes } where:
//   nodes: the full recursion tree — { id, parent, lo, hi, depth } (stable layout)
//   steps: linear timeline — each step carries everything the UI needs:
//     { line, phase, description, activeId, stackIds, revealedIds, array }
export function generateMergeSortSteps(input) {
  const steps = [];
  const nodes = [];
  const arr = [...input];
  const stack = []; // ids of active call frames (top = currently executing)
  const revealed = []; // ids of nodes that have appeared so far (tree grows)
  let idCounter = 0;

  const push = (line, phase, description, activeId, meta = {}) => {
    steps.push({
      line,
      phase,
      description,
      activeId,
      stackIds: [...stack],
      revealedIds: [...revealed],
      array: [...arr],
      ...meta, // e.g. { write: k } — the index just written during a merge
    });
  };

  function mergeSort(lo, hi, depth, parent) {
    const id = idCounter++;
    nodes.push({ id, parent, lo, hi, depth });
    revealed.push(id);
    stack.push(id);
    push(1, "call", `mergeSort(lo=${lo}, hi=${hi}) → work on [${arr.slice(lo, hi + 1).join(", ")}]`, id);

    // Base case: a single element (or empty) is already sorted.
    if (lo >= hi) {
      push(2, "base", `Base case: [${arr[lo]}] is a single element — already sorted.`, id);
      stack.pop();
      push(7, "return", `Return — [${arr[lo]}] bubbles back up to its parent.`, id);
      return;
    }

    const mid = (lo + hi) >> 1;
    push(3, "split", `mid = ${mid}. Split into left [${lo}…${mid}] and right [${mid + 1}…${hi}].`, id);

    push(4, "recurse-left", `Recurse into the LEFT half [${arr.slice(lo, mid + 1).join(", ")}].`, id);
    mergeSort(lo, mid, depth + 1, id);

    // Control has returned to this frame — highlight it again before going right.
    push(5, "recurse-right", `Left half done. Recurse into the RIGHT half [${arr.slice(mid + 1, hi + 1).join(", ")}].`, id);
    mergeSort(mid + 1, hi, depth + 1, id);

    push(6, "merge", `Both halves sorted. Call merge(${lo}, ${mid}, ${hi}) to combine them.`, id);
    merge(lo, mid, hi, id);

    stack.pop();
    push(7, "return", `Merged → [${arr.slice(lo, hi + 1).join(", ")}]. Return this sorted subarray.`, id);
  }

  // Per-comparison instrumented merge: emits a step for every compare/placement
  // so the code line-tracking follows the real work and node labels fill in order.
  function merge(lo, mid, hi, id) {
    const L = arr.slice(lo, mid + 1);
    push(10, "merge-init", `Copy the left half → L = [${L.join(", ")}].`, id);

    const R = arr.slice(mid + 1, hi + 1);
    push(11, "merge-init", `Copy the right half → R = [${R.join(", ")}].`, id);

    let i = 0;
    let j = 0;
    let k = lo;
    push(12, "merge-init", `Pointers: i=0 (into L), j=0 (into R), k=${lo} (write position).`, id);
    push(13, "merge-loop", `Repeatedly compare the fronts of L and R, writing the smaller.`, id);

    while (i < L.length && j < R.length) {
      if (L[i] <= R[j]) {
        const v = L[i];
        arr[k] = L[i];
        push(14, "merge-take-left", `L[${i}]=${v} ≤ R[${j}]=${R[j]} → write ${v} at index ${k}.`, id, { write: k });
        k++;
        i++;
      } else {
        const v = R[j];
        arr[k] = R[j];
        push(15, "merge-take-right", `L[${i}]=${L[i]} > R[${j}]=${v} → write ${v} at index ${k}.`, id, { write: k });
        k++;
        j++;
      }
    }

    while (i < L.length) {
      const v = L[i];
      arr[k] = L[i];
      push(17, "merge-drain", `R is empty — copy remaining L[${i}]=${v} to index ${k}.`, id, { write: k });
      k++;
      i++;
    }

    while (j < R.length) {
      const v = R[j];
      arr[k] = R[j];
      push(18, "merge-drain", `L is empty — copy remaining R[${j}]=${v} to index ${k}.`, id, { write: k });
      k++;
      j++;
    }
  }

  mergeSort(0, input.length - 1, 0, null);
  return { steps, nodes };
}
